import { supabase } from '@/integrations/supabase/client';
import type { TablesInsert } from '@/integrations/supabase/types';
import { determineStandardAssetClass } from '@/utils/asset-class';
import { createPortfolio as createPortfolioSvc } from '@/services/portfolio/PortfolioService';
import { insertAllocation } from '@/services/portfolio/PortfolioAllocationsService';
import { getDefaultCurrency } from '@/services/profile/UserProfileService';
import { useAuthStore } from '@/stores/authStore';

export async function hydrateFundDetails(fundId: string, baseFund: any): Promise<any | null> {
  try {
    const hasSaClass = !!(baseFund?.sa_asset_class && String(baseFund.sa_asset_class).trim());
    const hasAssetClass = !!(baseFund?.asset_class && String(baseFund.asset_class).trim());
    if (hasSaClass || hasAssetClass) {
      return null; // Already classified
    }

    const { data: rawData, error: rawError } = await supabase
      .from('funds_raw')
      .select('id, fund_name, sa_asset_class')
      .eq('id', fundId)
      .single();

    if (rawData) {
      return {
        ...baseFund,
        fund_name: baseFund?.fund_name || rawData.fund_name,
        sa_asset_class: rawData.sa_asset_class,
      };
    }

    if (rawError) {
      // eslint-disable-next-line no-console
      console.warn('Could not find in funds_raw, falling back to funds:', rawError);
    }

    const { data: fundsData, error: fundsError } = await supabase
      .from('funds')
      .select('id, fund_name, asset_class')
      .eq('id', fundId)
      .single();

    if (fundsData) {
      return {
        ...baseFund,
        fund_name: baseFund?.fund_name || (fundsData as any).fund_name,
        sa_asset_class: (fundsData as any).asset_class,
      };
    }

    if (fundsError) {
      // eslint-disable-next-line no-console
      console.warn('Fund not found in funds table either:', fundsError);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error hydrating fund details:', e);
  }
  return null;
}

export async function resolvePortfolioCurrency(portfolioId: string): Promise<string> {
  let currencyCode: string = 'CAD';
  try {
    const lsKey = `portfolio:currency:${portfolioId}`;
    const fromLs = typeof window !== 'undefined' ? window.localStorage.getItem(lsKey) : null;
    if (fromLs && fromLs.trim()) {
      currencyCode = fromLs.trim().toUpperCase();
    } else {
      const uid = useAuthStore.getState().user?.id;
      const dc = uid ? await getDefaultCurrency(uid) : null;
      if (dc) currencyCode = dc;
    }
  } catch {
    // ignore
  }
  return currencyCode;
}

export function buildInsertPayload(
  fundToAdd: any,
  targetPortfolioId: string,
  targetPortfolioType: string | undefined,
  currencyCode: string
): TablesInsert<'portfolio_allocations'> {
  const rawClass = fundToAdd.sa_asset_class || (fundToAdd as any).asset_class || '';
  const assetType = determineStandardAssetClass(rawClass, undefined) || 'unclassified';

  const insertPayload: TablesInsert<'portfolio_allocations'> = {
    portfolio_id: targetPortfolioId,
    asset_type: assetType,
    asset_id: fundToAdd.id,
    allocation_percentage: 0,
    currency_code: currencyCode,
  };
  if ((targetPortfolioType || 'model') !== 'model') {
    insertPayload.allocation_value = 0;
    insertPayload.is_value_locked = true;
  }
  return insertPayload;
}

export async function createPortfolio(userId: string, name: string, type: string) {
  const id = await createPortfolioSvc({ userId, name: name.trim(), portfolioType: type || 'model' });
  return { id } as any;
}

export async function addFundsToPortfolioBulk(params: {
  funds: any[];
  targetPortfolioId: string;
  targetPortfolioType: string | undefined;
  currencyCode: string;
  onProgress?: (info: { current: number; total: number; success: number; failed: number; errors: string[] }) => void;
}) {
  const { funds, targetPortfolioId, targetPortfolioType, currencyCode, onProgress } = params;
  const total = funds?.length || 0;
  let success = 0;
  let failed = 0;
  const errors: string[] = [];

  for (let i = 0; i < total; i++) {
    const fundToAdd = funds[i];
    try {
      const insertPayload = buildInsertPayload(fundToAdd, targetPortfolioId, targetPortfolioType, currencyCode);
      // Use service layer for insert to centralize DB access
      await insertAllocation(insertPayload);
      success++;
    } catch (e: any) {
      failed++;
      errors.push(`${fundToAdd.fund_name || (fundToAdd as any).name || fundToAdd.id}: ${e?.message || 'Unknown error'}`);
    }
    onProgress?.({ current: i + 1, total, success, failed, errors: [...errors] });
  }

  return { successCount: success, failedCount: failed, errors };
}
